import { withRetry } from '@willbooster/shared-lib';
import { z } from 'zod';

import { logger } from '@/infrastructures/pino';
import { dayjs } from '@/utils/dayjs';

const url = process.env.NEXT_PUBLIC_MOTI_AI_URL;
const motiRequestTimeoutMilliseconds = 10_000;
const motiRetryDelaysMilliseconds = [2000, 4000, 8000, 16_000] as const;

export interface MotiOutcomeInput {
  actorId: string;
  type: string;
  delta: number;
  actedAt?: string;
  tags?: readonly string[];
}

export interface MotiActorInput {
  displayName?: string;
}

export interface MotiLeaderboardInput {
  type: string;
  tag?: string;
  since?: string;
  until?: string;
  aggregateMethod?: 'total' | 'mean';
  top?: {
    take: number;
  };
  focus?: {
    actorId: string;
    take: number;
  };
}

const motiActorSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const motiLeaderboardEntrySchema = z.object({
  actorId: z.string(),
  displayName: z.string(),
  aggregateValue: z.number(),
  rank: z.number(),
});

const motiFocusLeaderboardEntrySchema = motiLeaderboardEntrySchema.extend({
  lastDelta: z.number(),
});

const motiLeaderboardResponseSchema = z.object({
  type: z.string(),
  top: z.array(motiLeaderboardEntrySchema).optional(),
  focus: z.array(motiFocusLeaderboardEntrySchema).optional(),
});

export type MotiActor = z.infer<typeof motiActorSchema>;
export type MotiLeaderboardResponse = z.infer<typeof motiLeaderboardResponseSchema>;

export async function createMotiOutcome(input: MotiOutcomeInput): Promise<void> {
  try {
    await postMoti('/outcomes', {
      ...input,
      actedAt: input.actedAt ?? dayjs().toISOString(),
    });
  } catch (error) {
    logger.warn({ err: error }, 'Failed to create Moti AI outcome after wake-up retries');
  }
}

export async function fetchMotiLeaderboard(input: MotiLeaderboardInput): Promise<MotiLeaderboardResponse> {
  return await postMoti('/leaderboard', input, motiLeaderboardResponseSchema);
}

export async function updateMotiActor(actorId: string, input: MotiActorInput): Promise<MotiActor> {
  return await postMoti(`/actors/${actorId}`, input, motiActorSchema);
}

async function postMoti(path: string, body: unknown): Promise<void>;
async function postMoti<T>(path: string, body: unknown, responseSchema: z.ZodType<T>): Promise<T>;
async function postMoti<T>(path: string, body: unknown, responseSchema?: z.ZodType<T>): Promise<T | void> {
  const requestBody = JSON.stringify(body);
  const motiUrl = buildMotiUrl(path);
  let failedCount = 0;

  return await withRetry(
    async (currentFailedCount) => {
      failedCount = currentFailedCount;
      const response = await fetchWithTimeout(motiUrl, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: requestBody,
      });

      if (!response.ok) {
        throw new MotiRequestError(path, response.status, response.statusText);
      }

      if (!responseSchema) {
        return;
      }

      const json: unknown = await response.json();
      return responseSchema.parse(json);
    },
    {
      handleError: async (error: unknown) => {
        const retryDelayMilliseconds = motiRetryDelaysMilliseconds[failedCount];
        // `withRetry` calls `handleError` before `shouldRetry`, so keep this guard to avoid waking Moti AI for final or non-retryable errors.
        if (retryDelayMilliseconds === undefined || !isRetryableMotiError(error)) return;

        logger.warn(
          { error },
          'Moti AI request failed for %s; waking service and retrying in %dms',
          path,
          retryDelayMilliseconds
        );
        void wakeMoti();
      },
      // `withRetry` counts the initial attempt, so four retry delays need five total attempts.
      retryCount: motiRetryDelaysMilliseconds.length + 1,
      shouldRetry: isRetryableMotiError,
      sleepMilliseconds: motiRetryDelaysMilliseconds[0],
      updateSleepMilliseconds: getNextMotiRetryDelayMilliseconds,
    }
  );
}

function getNextMotiRetryDelayMilliseconds(currentSleepMilliseconds: number): number {
  const currentIndex = (motiRetryDelaysMilliseconds as readonly number[]).indexOf(currentSleepMilliseconds);
  return motiRetryDelaysMilliseconds[currentIndex + 1] ?? 0;
}

function buildMotiUrl(path: string): string {
  return new URL(`/exercode${path}`, url).toString();
}

async function wakeMoti(): Promise<void> {
  try {
    const response = await fetchWithTimeout(new URL('/', url).toString(), {
      method: 'GET',
      cache: 'no-store',
    });
    if (!response.ok) {
      logger.warn('Moti AI wake-up probe returned %d %s', response.status, response.statusText);
    }
  } catch (error) {
    logger.warn({ err: error }, 'Moti AI wake-up probe failed');
  }
}

function isRetryableMotiError(error: unknown): boolean {
  if (error instanceof MotiRequestError) {
    return [408, 425, 429, 500, 502, 503, 504].includes(error.status);
  }

  return error instanceof TypeError || error instanceof SyntaxError || isAbortError(error);
}

async function fetchWithTimeout(input: string, init: RequestInit): Promise<Response> {
  return await fetch(input, {
    ...init,
    signal: AbortSignal.timeout(motiRequestTimeoutMilliseconds),
  });
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && (error.name === 'TimeoutError' || error.name === 'AbortError');
}

class MotiRequestError extends Error {
  constructor(
    path: string,
    readonly status: number,
    statusText: string
  ) {
    super(`Moti AI request failed for ${path}: ${status} ${statusText}`);
    this.name = 'MotiRequestError';
  }
}
